/**
 * Registro central das fontes exportáveis do sistema
 * -----------------------------------------------------------------------------
 * Para habilitar a exportação em um novo módulo basta criar a fonte em
 * `src/lib/export/sources/` e registrá-la aqui — o modal, os formatos, as
 * permissões e a auditoria já funcionam automaticamente.
 *
 * @version 1.3.0
 * RELEASE: 2026-10-14 v1.3.0 - Registra a fonte 'stock_chip_cancellations'.
 * RELEASE: 2026-10-02 v1.2.0 - Registra a fonte 'flow_sessions' (Fluxos — Sessões).
 * RELEASE: 2026-09-12 v1.1.0 - Registra as quatro fontes da Central de
 *          Importação de Listas (contatos, negócios, clientes e afiliados).
 * RELEASE: 2026-09-07 v1.0.0 - Criação inicial com a fonte 'crm_deals'.
 */

import type { ExportSource } from './types';
import { crmDealsExportSource } from './sources/crmDealsSource';
import {
  listsAffiliatesExportSource,
  listsContactsExportSource,
  listsCustomersExportSource,
  listsDealsExportSource,
} from './sources/listsSource';
import { flowSessionsExportSource } from './sources/flowSessionsSource';
// 2026-10-06: Estoque (rastreadores, chips, kits e movimentações)
import { stockChipsExportSource, stockKitsExportSource, stockMovementsExportSource, stockTrackersExportSource } from './sources/stockSource';

// 2026-10-08: Ordens de Serviço (listagem)
import { serviceOrdersExportSource } from './sources/serviceOrdersSource';
// 2026-10-14: Gestão de Chip — Cancelamento
import { chipCancellationsExportSource } from './sources/chipCancellationsSource';

/** Mapa de fontes exportáveis por identificador */
export const EXPORT_SOURCES: Record<string, ExportSource<never>> = {
  [crmDealsExportSource.id]: crmDealsExportSource as ExportSource<never>,
  [listsContactsExportSource.id]: listsContactsExportSource as unknown as ExportSource<never>,
  [listsDealsExportSource.id]: listsDealsExportSource as unknown as ExportSource<never>,
  [listsCustomersExportSource.id]: listsCustomersExportSource as unknown as ExportSource<never>,
  [listsAffiliatesExportSource.id]: listsAffiliatesExportSource as unknown as ExportSource<never>,
  [flowSessionsExportSource.id]: flowSessionsExportSource as unknown as ExportSource<never>,
  [stockTrackersExportSource.id]: stockTrackersExportSource as unknown as ExportSource<never>,
  [stockChipsExportSource.id]: stockChipsExportSource as unknown as ExportSource<never>,
  [stockKitsExportSource.id]: stockKitsExportSource as unknown as ExportSource<never>,
  [stockMovementsExportSource.id]: stockMovementsExportSource as unknown as ExportSource<never>,
  [serviceOrdersExportSource.id]: serviceOrdersExportSource as unknown as ExportSource<never>,
  [chipCancellationsExportSource.id]: chipCancellationsExportSource as unknown as ExportSource<never>,
};

/** Lista de fontes exportáveis (usada na tela de perfis de permissão) */
export const EXPORT_SOURCE_LIST = Object.values(EXPORT_SOURCES);

/** Recupera uma fonte pelo id (lança quando não registrada) */
export function getExportSource(id: string): ExportSource<never> {
  const fonte = EXPORT_SOURCES[id];
  if (!fonte) throw new Error(`Fonte de exportação não registrada: ${id}`);
  return fonte;
}
